/**
 * 後台 — 叫貨批次頁(一個下單日 = 一批)
 *
 * 流程:看下單建議 → 向工廠下單(原料自動扣)→ 工廠到貨登記 → 依名單替每位會員出貨
 * (每位會員一筆存摺出貨:扣燕窩存摺、推 LINE 出貨通知)→ 匯出 Excel。
 * 名單沒「固定」前(materialized = false)是依會員配送設定即時計算;改名單、填其他需求、確認下單都會順手固定。
 * 批次出貨 = 前端把會員切成 20 位一組、一組一次呼叫;失敗的會員留在名單上顯示原因,不迴圈重打。
 *
 * Route: /admin/supply/batches/:id(:id = 下單日 YYYY-MM-DD)
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Download, RefreshCw, X } from "lucide-react";
import AdminHeader from "../../components/AdminHeader";
import EmptyState from "../../components/EmptyState";
import LoadingScreen from "../../components/LoadingScreen";
import MemberSearchBox from "../../components/MemberSearchBox";
import SupplyStatusPill from "../../components/SupplyStatusPill";
import {
  adminSupplyAddArrival,
  adminSupplyCancelOrder,
  adminSupplyConfirmOrder,
  adminSupplyDeleteArrival,
  adminSupplyExport,
  adminSupplyGetBatch,
  adminSupplyRegenerate,
  adminSupplyResolveClaim,
  adminSupplySetLines,
  adminSupplyShip,
  adminSupplyUpdateBatch,
  CARRIER_OPTIONS,
  type SupplyBatchDetail,
  type SupplyLine,
  type SupplyLinePayload,
  type SupplyQty,
} from "../../api/admin";
import type { Member } from "../../api/members";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpe } from "../../utils/date";
import {
  CAT_NAME,
  fmtMD,
  fmtMDHM,
  fmtNum,
  parseQty,
  qtyText,
  shipFailText,
  supplyErrorText,
  weekdayLabel,
} from "../../utils/supply";


const SHIP_CHUNK = 20;   // 一次呼叫送幾位(後端上限 25)
const BLOCKING_FLAGS = ["member_suspended", "member_merged", "member_missing"];
const MEMBER_FLAG_TEXT: Record<string, string> = {
  member_suspended: "會員已停用",
  member_merged: "會員已合併",
  member_missing: "會員不存在",
};

const round1 = (n: number) => Math.round(n * 10) / 10;


export default function SupplyBatch() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [detail, setDetail] = useState<SupplyBatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  async function load() {
    try {
      setDetail(await adminSupplyGetBatch(id));
      setNotFound(false);
    } catch (e: any) {
      if (e?.response?.status === 404) setNotFound(true);
      else showToast("批次資料載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, id]);

  async function exportExcel() {
    setExporting(true);
    try {
      const blob = await adminSupplyExport(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `叫貨出貨_${id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("已匯出 Excel", "success");
    } catch {
      showToast("匯出失敗,請再試一次", "error");
    } finally {
      setExporting(false);
    }
  }

  async function regenerate() {
    if (!confirm("依目前的會員配送設定重新產生這一批名單?\n手動調整過的名單會被取代(已出貨的會員保留);還沒到貨、沒出貨的話,日期也會依目前規則重算。")) {
      return;
    }
    setRegenBusy(true);
    try {
      const r = await adminSupplyRegenerate(id);
      if (r.status === "ok") {
        showToast("已依配送設定重新產生名單", "success");
        await load();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("重新產生失敗,請稍後再試", "error");
    } finally {
      setRegenBusy(false);
    }
  }

  if (loading && !detail) return <LoadingScreen />;

  if (!detail) {
    return (
      <div className="min-h-screen bg-cream pb-12">
        <AdminHeader active="supply" />
        <div className="p-6">
          <button onClick={() => navigate("/admin/supply")} className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink">
            <ArrowLeft size={16} /> 回叫貨出貨
          </button>
          <EmptyState title={notFound ? "找不到這個批次" : "批次資料載入失敗"} hint={notFound ? "批次編號是下單日,格式 YYYY-MM-DD" : "請重新整理再試一次"} />
        </div>
      </div>
    );
  }

  const d = detail;
  const steps = [
    { label: "下單", date: d.order_date },
    { label: "工廠出貨", date: d.factory_ship_date },
    { label: "到貨", date: d.arrival_date },
    { label: "會員出貨", date: d.ship_date },
  ];

  return (
    <div className="min-h-screen bg-cream pb-12">
      <AdminHeader active="supply" />

      <div className="p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate("/admin/supply")}
              className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
            >
              <ArrowLeft size={16} /> 叫貨出貨
            </button>
            <h2 className="text-lg font-bold text-ink">{fmtMD(d.order_date)} 下單批次</h2>
            <SupplyStatusPill status={d.status} label={d.status_label} />
          </div>
          <div className="flex flex-wrap gap-2">
            {d.materialized && d.status !== "shipped" && (
              <button
                onClick={regenerate}
                disabled={regenBusy}
                className="btn-secondary inline-flex items-center gap-1.5 text-sm py-2 disabled:opacity-50"
              >
                <RefreshCw size={14} /> {regenBusy ? "產生中…" : "依配送設定重新產生名單"}
              </button>
            )}
            <button
              onClick={exportExcel}
              disabled={exporting}
              className="btn-primary inline-flex items-center gap-1.5 text-sm py-2 disabled:opacity-50"
            >
              <Download size={14} /> {exporting ? "匯出中…" : "匯出 Excel"}
            </button>
          </div>
        </div>

        {!d.materialized && (
          <div className="border border-sage bg-sage-light/40 rounded-lg p-3 text-sm text-ink">
            名單依會員配送設定即時計算;修改名單、填其他需求或確認下單後,這一批的名單就會固定。
          </div>
        )}

        {/* 日期線 */}
        <div className="card">
          <div className="flex flex-wrap items-center gap-2">
            {steps.map((s, i) => (
              <div key={s.label} className="flex items-center gap-2 flex-1 min-w-[8.5rem]">
                <div className="flex-1 bg-cream rounded-lg px-4 py-3">
                  <div className="text-xs text-muted">{s.label}</div>
                  <div className="mt-0.5 whitespace-nowrap">
                    <span className="text-lg font-bold text-ink">{fmtMD(s.date, true)}</span>
                    <span className="ml-1.5 text-sm text-muted">{weekdayLabel(s.date)}</span>
                  </div>
                </div>
                {i < steps.length - 1 && <ChevronRight size={18} className="text-muted shrink-0 hidden sm:block" />}
              </div>
            ))}
          </div>
        </div>

        <SuggestCard
          key={`sug-${JSON.stringify([d.extra, d.buffer, d.stock_offset, d.note])}`}
          d={d}
          reload={load}
        />
        <OrderCard
          key={`order-${JSON.stringify([d.ordered, d.suggested])}`}
          d={d}
          reload={load}
        />
        <ArrivalCard d={d} reload={load} />
        <ShipCard d={d} reload={load} />
      </div>
    </div>
  );
}


// ── 下單建議 ──────────────────────────────

type QtyText = { drink: string; fresh: string };

function toText(q: SupplyQty): QtyText {
  return { drink: String(q.drink), fresh: String(q.fresh) };
}

function SuggestCard({ d, reload }: { d: SupplyBatchDetail; reload: () => Promise<void> }) {
  const showToast = useUIStore((s) => s.showToast);
  const [extra, setExtra] = useState<QtyText>(toText(d.extra));
  const [buffer, setBuffer] = useState<QtyText>(toText(d.buffer));
  const [offset, setOffset] = useState<QtyText>(toText(d.stock_offset));
  const [note, setNote] = useState(d.note || "");
  const [busy, setBusy] = useState(false);

  // 輸入框打到一半不合法時,先當 0 算(存檔才擋)
  const n = (t: string) => parseQty(t) ?? 0;
  const suggested: SupplyQty = {
    drink: Math.max(0, d.need.drink + n(extra.drink) + n(buffer.drink) - n(offset.drink)),
    fresh: Math.max(0, d.need.fresh + n(extra.fresh) + n(buffer.fresh) - n(offset.fresh)),
  };
  const gpb = d.grams_per_bottle;
  const needGrams = round1(suggested.drink * gpb.drink + suggested.fresh * gpb.fresh);
  const check = d.raw_check;
  const balance = check && check.status !== "deducted" ? check.balance_before : null;

  async function save() {
    const e = { drink: parseQty(extra.drink), fresh: parseQty(extra.fresh) };
    const b = { drink: parseQty(buffer.drink), fresh: parseQty(buffer.fresh) };
    const o = { drink: parseQty(offset.drink), fresh: parseQty(offset.fresh) };
    if ([e.drink, e.fresh, b.drink, b.fresh, o.drink, o.fresh].some((v) => v === null)) {
      showToast("瓶數請填 0 以上的整數", "error");
      return;
    }
    setBusy(true);
    try {
      const r = await adminSupplyUpdateBatch(d.batch_id, {
        extra: e as SupplyQty,
        buffer: b as SupplyQty,
        stock_offset: o as SupplyQty,
        note: note.trim(),
      });
      if (r.status === "ok") {
        showToast("下單建議已儲存", "success");
        await reload();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("儲存失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  const input = (value: string, onChange: (v: string) => void) => (
    <input
      className="input w-24 text-right py-2"
      type="number"
      min={0}
      step={1}
      value={value}
      onChange={(ev) => onChange(ev.target.value)}
    />
  );

  return (
    <section className="card space-y-4">
      <div className="text-sm font-bold text-ink">下單建議</div>

      <div className="overflow-x-auto">
        <table className="text-sm">
          <thead>
            <tr className="text-muted text-xs">
              <th className="text-left font-medium pr-6 py-1"></th>
              <th className="text-right font-medium px-2 py-1 w-28">即飲</th>
              <th className="text-right font-medium px-2 py-1 w-28">鮮燉</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-line">
              <td className="pr-6 py-2 text-muted whitespace-nowrap">會員名單</td>
              <td className="px-2 py-2 text-right">{d.need.drink}</td>
              <td className="px-2 py-2 text-right">{d.need.fresh}</td>
            </tr>
            <tr className="border-t border-line">
              <td className="pr-6 py-1.5 text-muted whitespace-nowrap">其他需求</td>
              <td className="px-2 py-1.5">{input(extra.drink, (v) => setExtra({ ...extra, drink: v }))}</td>
              <td className="px-2 py-1.5">{input(extra.fresh, (v) => setExtra({ ...extra, fresh: v }))}</td>
            </tr>
            <tr className="border-t border-line">
              <td className="pr-6 py-1.5 text-muted whitespace-nowrap">緩衝</td>
              <td className="px-2 py-1.5">{input(buffer.drink, (v) => setBuffer({ ...buffer, drink: v }))}</td>
              <td className="px-2 py-1.5">{input(buffer.fresh, (v) => setBuffer({ ...buffer, fresh: v }))}</td>
            </tr>
            <tr className="border-t border-line">
              <td className="pr-6 py-1.5 text-muted whitespace-nowrap">扣除現有成品</td>
              <td className="px-2 py-1.5">{input(offset.drink, (v) => setOffset({ ...offset, drink: v }))}</td>
              <td className="px-2 py-1.5">{input(offset.fresh, (v) => setOffset({ ...offset, fresh: v }))}</td>
            </tr>
            <tr className="border-t-2 border-line font-bold text-ink">
              <td className="pr-6 py-2 whitespace-nowrap">建議下單</td>
              <td className="px-2 py-2 text-right">{suggested.drink}</td>
              <td className="px-2 py-2 text-right">{suggested.fresh}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="text-sm">
        {d.raw_check?.status === "deducted" ? (
          <span className="text-muted">原料:已扣 {fmtNum(d.raw_grams_used)} 克</span>
        ) : (
          <>
            <span className="text-ink">原料:需要 {fmtNum(needGrams)} 克</span>
            {balance !== null && (
              <>
                <span className="text-muted"> · 結餘 {fmtNum(balance)} 克 · </span>
                {needGrams > balance ? (
                  <span className="text-coral">不足 差 {fmtNum(round1(needGrams - balance))} 克</span>
                ) : (
                  <span className="text-primary">足夠</span>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div>
        <label className="label">備註</label>
        <textarea
          className="input"
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div className="flex justify-end">
        <button onClick={save} className="btn-primary text-sm py-2" disabled={busy}>
          {busy ? "儲存中…" : "儲存下單建議"}
        </button>
      </div>
    </section>
  );
}


// ── 工廠下單 ──────────────────────────────

function OrderCard({ d, reload }: { d: SupplyBatchDetail; reload: () => Promise<void> }) {
  const showToast = useUIStore((s) => s.showToast);
  const base = d.ordered ?? d.suggested;
  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState<QtyText>(toText(base));
  const [busy, setBusy] = useState(false);

  const ordered = d.ordered;
  const canCancel = !!ordered && d.arrivals.length === 0 && d.shipped_count === 0;
  const showForm = !ordered || editing;

  const parsed = { drink: parseQty(qty.drink), fresh: parseQty(qty.fresh) };
  const previewGrams =
    parsed.drink !== null && parsed.fresh !== null
      ? round1(parsed.drink * d.grams_per_bottle.drink + parsed.fresh * d.grams_per_bottle.fresh)
      : null;

  async function submit() {
    if (parsed.drink === null || parsed.fresh === null) {
      showToast("下單瓶數請填 0 以上的整數", "error");
      return;
    }
    const q: SupplyQty = { drink: parsed.drink, fresh: parsed.fresh };
    if (q.drink + q.fresh <= 0) {
      showToast(supplyErrorText({ status: "empty_order" }), "error");
      return;
    }
    const msg = ordered
      ? `確認將下單瓶數改為 ${qtyText(q)}?原料會依差額補扣或退回`
      : "確認已向工廠下單?原料會依瓶數自動扣除";
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      const r = await adminSupplyConfirmOrder(d.batch_id, q);
      if (r.status === "ok") {
        if (r.raw_short) showToast("原料結餘不足,請到原料帳登記進貨或盤點", "error");
        else showToast(ordered ? "已修改下單瓶數" : "已記錄下單,原料已扣除", "success");
        setEditing(false);
        await reload();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("送出失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  async function cancelOrder() {
    if (!confirm("取消這一批的下單?已扣的原料會退回。")) return;
    setBusy(true);
    try {
      const r = await adminSupplyCancelOrder(d.batch_id);
      if (r.status === "ok") {
        showToast("已取消下單,原料已退回", "success");
        await reload();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("取消失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card space-y-3">
      <div className="text-sm font-bold text-ink">工廠下單</div>

      {ordered && !editing && (
        <>
          <div className="text-sm text-ink">
            已下單 <b>即飲 {ordered.drink}</b> / <b>鮮燉 {ordered.fresh}</b>
            <span className="ml-2 text-xs text-muted">
              ({d.ordered_by || "—"},{fmtMDHM(d.ordered_at)})
            </span>
          </div>
          <div className="text-xs text-muted">已扣原料 {fmtNum(d.raw_grams_used)} 克</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setQty(toText(ordered));
                setEditing(true);
              }}
              className="btn-secondary text-sm py-2"
              disabled={busy}
            >
              修改下單瓶數
            </button>
            {canCancel && (
              <button
                onClick={cancelOrder}
                className="text-sm px-5 py-2 rounded-full border border-coral/50 text-coral hover:bg-coral/10 disabled:opacity-50"
                disabled={busy}
              >
                取消下單
              </button>
            )}
          </div>
          {!canCancel && (
            <div className="text-xxs text-muted">已有到貨或出貨紀錄,不能取消下單;瓶數可以修改。</div>
          )}
        </>
      )}

      {showForm && (
        <>
          {!ordered && <div className="text-xs text-muted">預設帶入建議下單瓶數,可以再調整。</div>}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="label">即飲(瓶)</label>
              <input
                className="input w-28"
                type="number"
                min={0}
                step={1}
                value={qty.drink}
                onChange={(e) => setQty({ ...qty, drink: e.target.value })}
              />
            </div>
            <div>
              <label className="label">鮮燉(瓶)</label>
              <input
                className="input w-28"
                type="number"
                min={0}
                step={1}
                value={qty.fresh}
                onChange={(e) => setQty({ ...qty, fresh: e.target.value })}
              />
            </div>
            <div className="text-xs text-muted pb-3">
              {previewGrams !== null && `約用原料 ${fmtNum(previewGrams)} 克`}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={submit} className="btn-primary text-sm py-2" disabled={busy}>
              {busy ? "送出中…" : ordered ? "儲存修改" : "確認已下單"}
            </button>
            {ordered && (
              <button onClick={() => setEditing(false)} className="btn-secondary text-sm py-2" disabled={busy}>
                取消
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}


// ── 工廠到貨 ──────────────────────────────

function ArrivalCard({ d, reload }: { d: SupplyBatchDetail; reload: () => Promise<void> }) {
  const showToast = useUIStore((s) => s.showToast);
  const [date, setDate] = useState(() => todayTpe());
  const [drink, setDrink] = useState("");
  const [dmgDrink, setDmgDrink] = useState("");
  const [fresh, setFresh] = useState("");
  const [dmgFresh, setDmgFresh] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const ordered = d.ordered;

  async function add() {
    const v = {
      drink: parseQty(drink),
      damaged_drink: parseQty(dmgDrink),
      fresh: parseQty(fresh),
      damaged_fresh: parseQty(dmgFresh),
    };
    if (Object.values(v).some((x) => x === null)) {
      showToast("瓶數請填 0 以上的整數", "error");
      return;
    }
    const q = v as { drink: number; damaged_drink: number; fresh: number; damaged_fresh: number };
    if (q.damaged_drink > q.drink || q.damaged_fresh > q.fresh) {
      showToast("破損瓶數不可超過實收瓶數", "error");
      return;
    }
    if (q.drink + q.fresh <= 0) {
      showToast(supplyErrorText({ status: "empty_arrival" }), "error");
      return;
    }
    setBusy(true);
    try {
      const r = await adminSupplyAddArrival(d.batch_id, { date, ...q, note: note.trim() });
      if (r.status === "ok") {
        showToast("已登記到貨", "success");
        setDrink("");
        setDmgDrink("");
        setFresh("");
        setDmgFresh("");
        setNote("");
        await reload();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("登記失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(arrivalId: string) {
    if (!confirm("刪除這筆到貨紀錄?")) return;
    try {
      const r = await adminSupplyDeleteArrival(d.batch_id, arrivalId);
      if (r.status === "ok") {
        showToast("已刪除到貨紀錄", "success");
        await reload();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("刪除失敗,請稍後再試", "error");
    }
  }

  // 短少 = 下單 - 實收(只列大於 0 的品類)
  const shortParts = ordered
    ? (["drink", "fresh"] as const)
        .map((c) => ({ c, n: ordered[c] - d.received[c] }))
        .filter((x) => x.n > 0)
    : [];
  // 可出良品 = 實收 - 破損;跟會員名單比,不夠就提醒(破損一瓶就少一位會員能出)
  const good = { drink: d.received.drink - d.damaged.drink, fresh: d.received.fresh - d.damaged.fresh };
  const lackParts = (["drink", "fresh"] as const)
    .map((c) => ({ c, n: d.need[c] - good[c] }))
    .filter((x) => x.n > 0);

  return (
    <section className="card space-y-4">
      <div className="text-sm font-bold text-ink">工廠到貨</div>

      {!ordered ? (
        <div className="text-sm text-muted">要先確認已向工廠下單,才能登記到貨。</div>
      ) : (
        <>
          {d.arrivals.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-line/40 text-muted">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">日期</th>
                    <th className="text-left font-medium px-3 py-2">即飲 實收 / 破損</th>
                    <th className="text-left font-medium px-3 py-2">鮮燉 實收 / 破損</th>
                    <th className="text-left font-medium px-3 py-2">備註</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {d.arrivals.map((a) => (
                    <tr key={a.arrival_id} className="border-t border-line">
                      <td className="px-3 py-2 whitespace-nowrap">{a.date}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {a.drink} / <span className={a.damaged_drink > 0 ? "text-coral" : ""}>{a.damaged_drink}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {a.fresh} / <span className={a.damaged_fresh > 0 ? "text-coral" : ""}>{a.damaged_fresh}</span>
                      </td>
                      <td className="px-3 py-2">{a.note || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => remove(a.arrival_id)} className="text-xs text-coral hover:underline">
                          刪除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div className="bg-cream rounded-lg px-3 py-2">
              <div className="text-xs text-muted">下單</div>
              <div className="whitespace-nowrap">{qtyText(ordered)}</div>
            </div>
            <div className="bg-cream rounded-lg px-3 py-2">
              <div className="text-xs text-muted">實收</div>
              <div className="whitespace-nowrap">{qtyText(d.received)}</div>
            </div>
            <div className="bg-cream rounded-lg px-3 py-2">
              <div className="text-xs text-muted">破損</div>
              <div className="whitespace-nowrap">{qtyText(d.damaged)}</div>
            </div>
            {shortParts.length > 0 && (
              <div className="bg-coral/5 border border-coral/30 rounded-lg px-3 py-2">
                <div className="text-xs text-muted">短少</div>
                <div className="text-coral whitespace-nowrap">
                  {shortParts.map((x) => `${CAT_NAME[x.c]} ${x.n}`).join(" / ")}
                </div>
              </div>
            )}
          </div>

          {d.arrivals.length > 0 &&
            d.status !== "shipped" &&
            (lackParts.length > 0 ? (
              <div className="text-sm text-coral">
                {`可出良品 ${qtyText(good)},比會員名單少 ${lackParts.map((x) => `${CAT_NAME[x.c]} ${x.n} 瓶`).join("、")};出貨前請調整名單或補貨。`}
              </div>
            ) : (
              <div className="text-xs text-muted">
                可出良品 {qtyText(good)},夠出會員名單({qtyText(d.need)})。
              </div>
            ))}

          <div className="border-t border-line pt-4 space-y-3">
            <div className="text-xs text-muted">登記一筆到貨(可分次到貨,每次一筆)</div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="label">到貨日期</label>
                <input className="input w-44" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <label className="label">即飲 實收</label>
                <input className="input w-24" type="number" min={0} step={1} value={drink} onChange={(e) => setDrink(e.target.value)} />
              </div>
              <div>
                <label className="label">其中破損</label>
                <input className="input w-24" type="number" min={0} step={1} value={dmgDrink} onChange={(e) => setDmgDrink(e.target.value)} />
              </div>
              <div>
                <label className="label">鮮燉 實收</label>
                <input className="input w-24" type="number" min={0} step={1} value={fresh} onChange={(e) => setFresh(e.target.value)} />
              </div>
              <div>
                <label className="label">其中破損</label>
                <input className="input w-24" type="number" min={0} step={1} value={dmgFresh} onChange={(e) => setDmgFresh(e.target.value)} />
              </div>
              <div className="flex-1 min-w-48">
                <label className="label">備註(選填)</label>
                <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
              </div>
              <button onClick={add} className="btn-primary text-sm py-2.5" disabled={busy}>
                {busy ? "登記中…" : "登記到貨"}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}


// ── 出貨名單 ──────────────────────────────

interface DraftLine {
  member_uid: string;
  name: string;
  phone: string;
  drink: string;
  fresh: string;
  note: string;
}

interface ShipRunResult {
  shipped: number;
  skipped: number;
  busy: number;      // 同一時間有別人在操作這一批,這次略過的(沒扣存摺,再按一次即可)
  failed: { uid: string; name: string; reason: string }[];
  aborted: string;
}

function lineName(ln: { name?: string; display_name?: string; member_uid: string }) {
  return ln.name || ln.display_name || ln.member_uid;
}

function LineStatus({ ln, onResolve }: { ln: SupplyLine; onResolve?: (ln: SupplyLine) => void }) {
  if (ln.shipped) {
    return <span className="text-primary whitespace-nowrap">已出貨 {fmtMD(ln.shipped.ship_date)}</span>;
  }
  if (ln.claim?.state === "shipping" || ln.claim?.state === "resolving") {
    return <span className="text-muted whitespace-nowrap">{ln.claim.state === "resolving" ? "查核中" : "出貨處理中"}</span>;
  }
  if (ln.claim?.state === "unknown") {
    return (
      <div className="text-xs text-coral space-y-1">
        <div className="whitespace-nowrap font-medium">出貨狀態不明</div>
        {onResolve && (
          <button onClick={() => onResolve(ln)} className="underline hover:no-underline">
            查核
          </button>
        )}
      </div>
    );
  }
  const items: string[] = [];
  if (ln.ship_error) items.push(`出貨未成功:${shipFailText(ln.ship_error)}`);
  for (const f of ln.flags) {
    if (MEMBER_FLAG_TEXT[f]) items.push(MEMBER_FLAG_TEXT[f]);
  }
  if (ln.flags.includes("wallet_short")) items.push("存摺不足");
  if (ln.flags.includes("missing_address")) items.push("缺地址");
  if (items.length === 0) {
    return <span className="text-muted whitespace-nowrap">{ln.drink + ln.fresh > 0 ? "待出貨" : "無瓶數"}</span>;
  }
  return (
    <div className="text-xs text-coral space-y-0.5">
      {items.map((t) => (
        <div key={t}>{t}</div>
      ))}
    </div>
  );
}

function ShipCard({ d, reload }: { d: SupplyBatchDetail; reload: () => Promise<void> }) {
  const showToast = useUIStore((s) => s.showToast);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [carrier, setCarrier] = useState(d.settings?.default_carrier || "");
  const [shipDate, setShipDate] = useState(d.ship_date || todayTpe());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<ShipRunResult | null>(null);

  const shipping = progress !== null;
  const lines = d.lines;
  // 已出貨、出貨處理中 / 狀態不明的列都鎖住:不能勾、不能改,存名單時原樣帶回
  const lockedLines = lines.filter((l) => l.shipped || l.claim);
  const unknownLines = lines.filter((l) => l.claim?.state === "unknown");

  const hasQty = (l: SupplyLine) => l.drink + l.fresh > 0;
  const blocked = (l: SupplyLine) => l.flags.some((f) => BLOCKING_FLAGS.includes(f));
  const checkable = (l: SupplyLine) => !l.shipped && !l.claim && hasQty(l) && !blocked(l);
  const checkableRows = lines.filter(checkable);
  const selectedUids = checkableRows.filter((l) => selected.has(l.member_uid)).map((l) => l.member_uid);
  const allChecked = checkableRows.length > 0 && selectedUids.length === checkableRows.length;

  function toggle(uid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(checkableRows.map((l) => l.member_uid)));
  }

  // ── 出貨狀態不明:查核 ──
  async function resolveClaim(ln: SupplyLine) {
    const ok = confirm(
      `查核「${lineName(ln)}」的出貨狀態?
` +
        "系統會用這次出貨的編號核對存摺:出貨單在,就補記為已出貨;出貨單不在,就把這次已扣的瓶數退回存摺,再解除鎖定,之後可以重新出貨。",
    );
    if (!ok) return;
    try {
      const r = await adminSupplyResolveClaim(d.batch_id, ln.member_uid);
      if (r.status === "ok") {
        const back = r.refunded ? qtyText(r.refunded) : "";
        const refundedAny = !!r.refunded && r.refunded.drink + r.refunded.fresh > 0;
        showToast(
          r.resolution === "shipped"
            ? "找到出貨單,已補記為已出貨"
            : refundedAny
              ? `這筆只扣到一部分,已退回存摺(${back})並解除鎖定,可以重新出貨`
              : "這筆沒有扣到存摺,已解除鎖定,可以重新出貨",
          "success",
        );
        await reload();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("查核失敗,請稍後再試", "error");
    }
  }

  // ── 編輯名單 ──
  function startEdit() {
    setDraft(
      lines
        .filter((l) => !l.shipped && !l.claim)
        .map((l) => ({
          member_uid: l.member_uid,
          name: lineName(l),
          phone: l.phone,
          drink: String(l.drink),
          fresh: String(l.fresh),
          note: l.note || "",
        })),
    );
    setSelected(new Set());
    setEditing(true);
  }

  function patchDraft(uid: string, p: Partial<DraftLine>) {
    setDraft((rows) => rows.map((r) => (r.member_uid === uid ? { ...r, ...p } : r)));
  }

  function addMember(m: Member) {
    const uid = m.line_user_id;
    if (draft.some((r) => r.member_uid === uid) || lockedLines.some((l) => l.member_uid === uid)) {
      showToast("這位會員已在名單中", "info");
      return;
    }
    setDraft((rows) => [
      ...rows,
      { member_uid: uid, name: m.name || m.display_name || "—", phone: m.phone || "", drink: "0", fresh: "0", note: "" },
    ]);
  }

  async function saveEdit() {
    const payload: SupplyLinePayload[] = [];
    for (const r of draft) {
      const drink = parseQty(r.drink);
      const fresh = parseQty(r.fresh);
      if (drink === null || fresh === null) {
        showToast("名單瓶數請填 0 以上的整數", "error");
        return;
      }
      payload.push({ member_uid: r.member_uid, drink, fresh, note: r.note.trim() });
    }
    // 鎖住的列原樣帶回(後端不論送什麼都會保留)
    for (const l of lockedLines) {
      payload.push({ member_uid: l.member_uid, drink: l.drink, fresh: l.fresh, note: l.note || "" });
    }
    setSaving(true);
    try {
      const r = await adminSupplySetLines(d.batch_id, payload);
      if (r.status === "ok") {
        showToast("名單已儲存", "success");
        setEditing(false);
        await reload();
      } else {
        const who = r.member_uid ? draft.find((x) => x.member_uid === r.member_uid)?.name : "";
        showToast(`${who ? `${who}:` : ""}${supplyErrorText(r)}`, "error");
      }
    } catch {
      showToast("儲存失敗,請稍後再試", "error");
    } finally {
      setSaving(false);
    }
  }

  // ── 批次出貨:每 20 位一次呼叫,不依 remaining 迴圈 ──
  async function runShip(uids: string[]) {
    if (uids.length === 0) return;
    if (!carrier) {
      showToast("請選擇物流方式", "error");
      return;
    }
    const shortCount = lines.filter((l) => uids.includes(l.member_uid) && l.flags.includes("wallet_short")).length;
    const noAddrCount = lines.filter((l) => uids.includes(l.member_uid) && l.flags.includes("missing_address")).length;
    const msg =
      `將為 ${uids.length} 位會員建立存摺出貨:扣除存摺瓶數,並推 LINE 出貨通知給會員。確定?` +
      (shortCount > 0 ? `\n其中 ${shortCount} 位的存摺可能不足,不足的會列為出貨未成功。` : "") +
      (noAddrCount > 0 ? `\n其中 ${noAddrCount} 位會員資料沒有收件地址,請確認已另外取得地址。` : "");
    if (!confirm(msg)) return;

    const nameOf = new Map(lines.map((l) => [l.member_uid, lineName(l)]));
    const failed: ShipRunResult["failed"] = [];
    let shipped = 0;
    let processed = 0;
    let busy = 0;
    let aborted = "";
    setResult(null);
    setProgress({ done: 0, total: uids.length });
    try {
      for (let i = 0; i < uids.length; i += SHIP_CHUNK) {
        const chunk = uids.slice(i, i + SHIP_CHUNK);
        const r = await adminSupplyShip(d.batch_id, { member_uids: chunk, carrier, ship_date: shipDate });
        if (r.status !== "ok") {
          aborted = supplyErrorText(r);
          break;
        }
        shipped += (r.shipped || []).length;
        busy += (r.skipped || []).filter((x) => x.reason === "busy").length;
        for (const f of r.failed || []) {
          failed.push({ uid: f.member_uid, name: nameOf.get(f.member_uid) || f.member_uid, reason: shipFailText(f) });
        }
        processed += chunk.length;
        setProgress({ done: processed, total: uids.length });
      }
    } catch {
      aborted = "連線中斷,請確認名單上的出貨狀態再繼續;已出貨的不會重複扣存摺";
    }
    setProgress(null);
    setSelected(new Set());
    setResult({ shipped, failed, busy, skipped: Math.max(0, processed - shipped - failed.length - busy), aborted });
    if (aborted) showToast(aborted, "error");
    await reload();
  }

  // ── 表格 ──
  const shownDraft = editing ? draft : [];
  const totalDrink = editing
    ? draft.reduce((s, r) => s + (parseQty(r.drink) ?? 0), 0) + lockedLines.reduce((s, l) => s + l.drink, 0)
    : lines.reduce((s, l) => s + l.drink, 0);
  const totalFresh = editing
    ? draft.reduce((s, r) => s + (parseQty(r.fresh) ?? 0), 0) + lockedLines.reduce((s, l) => s + l.fresh, 0)
    : lines.reduce((s, l) => s + l.fresh, 0);
  const rowCount = editing ? draft.length + lockedLines.length : lines.length;

  const th = "text-left font-medium px-3 py-2.5 whitespace-nowrap";

  function renderStaticCells(ln: SupplyLine) {
    return (
      <>
        <td className="px-3 py-2.5 whitespace-nowrap">
          <div className="font-medium text-ink">{ln.name || "—"}</div>
          {ln.display_name && <div className="text-xs text-muted">{ln.display_name}</div>}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap">{ln.phone || "—"}</td>
        <td className="px-3 py-2.5">
          {ln.address_ok ? <span>{ln.address_text}</span> : <span className="text-coral whitespace-nowrap">缺地址</span>}
        </td>
      </>
    );
  }

  function renderAvailable(ln: SupplyLine | undefined) {
    if (!ln || !ln.available) return <span className="text-muted">—</span>;
    const short = ln.flags.includes("wallet_short");
    return (
      <span className={`whitespace-nowrap ${short ? "text-coral font-medium" : ""}`}>
        {ln.available.drink} / {ln.available.fresh}
      </span>
    );
  }

  return (
    <section className="card space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm font-bold text-ink">
          出貨名單
          <span className="ml-2 text-xs font-normal text-muted">
            已出貨 {d.shipped_count} / 共 {d.line_count} 位
          </span>
        </div>
        {d.status !== "shipped" &&
          (editing ? (
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="btn-secondary text-sm py-1.5 px-4" disabled={saving}>
                取消
              </button>
              <button onClick={saveEdit} className="btn-primary text-sm py-1.5 px-4" disabled={saving}>
                {saving ? "儲存中…" : "儲存名單"}
              </button>
            </div>
          ) : (
            <button onClick={startEdit} className="btn-secondary text-sm py-1.5 px-4" disabled={shipping}>
              編輯名單
            </button>
          ))}
      </div>

      {editing && (
        <div className="space-y-2">
          <div className="text-xs text-muted">
            可以改瓶數與備註、移除會員、加入新會員;已出貨的會員不能改。存檔後才會生效。
          </div>
          <div className="max-w-md">
            <MemberSearchBox onPick={addMember} placeholder="加入會員:輸入手機號碼(或姓名)搜尋" />
          </div>
        </div>
      )}

      {/* 出貨控制 */}
      {!editing && (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">物流</label>
            <select className="input w-44 py-2" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
              <option value="">請選擇</option>
              {CARRIER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">出貨日</label>
            <input className="input w-44 py-2" type="date" value={shipDate} onChange={(e) => setShipDate(e.target.value)} />
          </div>
          <button
            onClick={() => runShip(selectedUids)}
            disabled={shipping || selectedUids.length === 0}
            className="btn-primary text-sm py-2.5"
          >
            出貨所選({selectedUids.length} 位)
          </button>
          <button
            onClick={() => runShip(checkableRows.map((l) => l.member_uid))}
            disabled={shipping || checkableRows.length === 0}
            className="btn-secondary text-sm py-2.5 disabled:opacity-50"
          >
            全部出貨
          </button>
          {progress && (
            <div className="text-sm text-muted pb-2.5">
              出貨中 {progress.done} / {progress.total} 位,請勿離開頁面…
            </div>
          )}
        </div>
      )}

      {unknownLines.length > 0 && !editing && (
        <div className="rounded-lg border border-coral/40 bg-coral/5 p-3 text-sm text-coral">
          {unknownLines.length} 位會員出貨狀態不明(出貨途中連線中斷,可能已扣存摺):
          {unknownLines.map((l) => lineName(l)).join("、")}。這幾位不會被重複出貨;請按該列的「查核」,系統會依出貨編號核對存摺,補記或退回。
        </div>
      )}

      {result && (
        <div className={`rounded-lg border p-3 space-y-1.5 text-sm ${result.failed.length > 0 || result.aborted ? "border-coral/40 bg-coral/5" : "border-sage bg-sage-light/40"}`}>
          <div className="flex items-start justify-between">
            <div className="font-medium text-ink">
              出貨結果:成功 {result.shipped} 位
              {result.failed.length > 0 && `,未成功 ${result.failed.length} 位`}
            </div>
            <button onClick={() => setResult(null)} className="text-muted hover:text-ink" aria-label="關閉">
              <X size={16} />
            </button>
          </div>
          {result.failed.map((f) => (
            <div key={f.uid} className="text-coral text-xs">
              {f.name}:{f.reason}
            </div>
          ))}
          {result.busy > 0 && (
            <div className="text-xs text-coral">
              {result.busy} 位剛好有其他人在操作這一批,這次沒有出貨(存摺沒扣),請再按一次出貨。
            </div>
          )}
          {result.skipped > 0 && (
            <div className="text-xs text-muted">另有 {result.skipped} 位略過(已出貨、出貨處理中或瓶數為 0)。</div>
          )}
          {result.aborted && <div className="text-xs text-coral">{result.aborted}</div>}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-line/40 text-muted">
            <tr>
              {!editing && (
                <th className="px-3 py-2.5 w-10">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    disabled={checkableRows.length === 0 || shipping}
                    onChange={toggleAll}
                    className="cursor-pointer"
                    aria-label="全選可出貨會員"
                  />
                </th>
              )}
              <th className={th}>會員</th>
              <th className={th}>電話</th>
              <th className={th}>地址</th>
              <th className={th}>即飲</th>
              <th className={th}>鮮燉</th>
              <th className={th}>存摺可用 即飲 / 鮮燉</th>
              <th className={th}>狀態</th>
              <th className={th}>備註</th>
              {editing && <th className="px-3 py-2.5"></th>}
            </tr>
          </thead>
          <tbody>
            {rowCount === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-10 text-muted text-sm">
                  這一批目前沒有名單,到「會員配送」設定每批瓶數,或按「編輯名單」手動加入
                </td>
              </tr>
            )}

            {/* 編輯模式:未出貨的列可改 */}
            {shownDraft.map((r) => {
              const ln = lines.find((l) => l.member_uid === r.member_uid);
              return (
                <tr key={r.member_uid} className="border-t border-line align-top">
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="font-medium text-ink">{r.name || "—"}</div>
                    {ln?.display_name && <div className="text-xs text-muted">{ln.display_name}</div>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.phone || "—"}</td>
                  <td className="px-3 py-2.5">
                    {ln ? (
                      ln.address_ok ? ln.address_text : <span className="text-coral whitespace-nowrap">缺地址</span>
                    ) : (
                      <span className="text-muted text-xs">儲存後顯示</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className="input w-20 text-right py-1.5"
                      type="number"
                      min={0}
                      step={1}
                      value={r.drink}
                      onChange={(e) => patchDraft(r.member_uid, { drink: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className="input w-20 text-right py-1.5"
                      type="number"
                      min={0}
                      step={1}
                      value={r.fresh}
                      onChange={(e) => patchDraft(r.member_uid, { fresh: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2.5">{renderAvailable(ln)}</td>
                  <td className="px-3 py-2.5">
                    {ln ? <LineStatus ln={ln} /> : <span className="text-muted text-xs">新加入</span>}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className="input w-40 py-1.5"
                      value={r.note}
                      maxLength={200}
                      onChange={(e) => patchDraft(r.member_uid, { note: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <button
                      onClick={() => setDraft((rows) => rows.filter((x) => x.member_uid !== r.member_uid))}
                      className="text-xs text-coral hover:underline"
                    >
                      移除
                    </button>
                  </td>
                </tr>
              );
            })}

            {/* 檢視模式:全部;編輯模式:只列鎖住的(已出貨 / 出貨中,不能改) */}
            {(editing ? lockedLines : lines).map((ln) => (
              <tr key={ln.member_uid} className="border-t border-line align-top">
                {!editing && (
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    {!ln.shipped && !ln.claim && hasQty(ln) && (
                      <input
                        type="checkbox"
                        checked={selected.has(ln.member_uid)}
                        disabled={blocked(ln) || shipping}
                        onChange={() => toggle(ln.member_uid)}
                        className="cursor-pointer"
                        aria-label={`選取 ${lineName(ln)}`}
                      />
                    )}
                  </td>
                )}
                {renderStaticCells(ln)}
                <td className="px-3 py-2.5">{ln.drink}</td>
                <td className="px-3 py-2.5">{ln.fresh}</td>
                <td className="px-3 py-2.5">{renderAvailable(ln)}</td>
                <td className="px-3 py-2.5"><LineStatus ln={ln} onResolve={editing ? undefined : resolveClaim} /></td>
                <td className="px-3 py-2.5">{ln.note || "—"}</td>
                {editing && <td className="px-3 py-2.5"></td>}
              </tr>
            ))}

            {rowCount > 0 && (
              <tr className="border-t-2 border-line font-bold text-ink bg-cream/50">
                {!editing && <td className="px-3 py-2.5"></td>}
                <td className="px-3 py-2.5" colSpan={3}>合計(共 {rowCount} 列)</td>
                <td className="px-3 py-2.5">{totalDrink}</td>
                <td className="px-3 py-2.5">{totalFresh}</td>
                <td className="px-3 py-2.5" colSpan={editing ? 4 : 3}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
